import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { ListTodo, Calendar, IndianRupee, Package, Clock, CheckCircle2, Circle, AlertCircle, Filter, Search, Wifi, WifiOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useRealtimeTaskUpdates, useWebSocket } from '@/contexts/WebSocketContext';
// iter-386u-j — 25/50/75/90/100 bracket colour palette shared with Vendor & Admin.
import { getProgressBgClass } from '@/components/ProgressSlider';

export default function ClientTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');

  const { isConnected } = useWebSocket();

  const fetchClientTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/client/tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClientTasks();
  }, [fetchClientTasks]);

  // 🔔 Real-time updates: Auto-refresh when vendor updates progress
  useRealtimeTaskUpdates(null, (update) => {
    // Refetch tasks when a progress update is received
    fetchClientTasks();
  });

  // Get unique projects for filter
  const projects = [...new Set(tasks.map(t => t.project_name))];

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = !searchTerm || 
      task.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesProject = projectFilter === 'all' || task.project_name === projectFilter;
    
    return matchesSearch && matchesStatus && matchesProject;
  });

  // Calculate stats
  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    notStarted: tasks.filter(t => t.status === 'not_started').length,
    totalValue: tasks.reduce((sum, t) => sum + (t.total || 0), 0)
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return <CheckCircle2 size={16} className="text-green-500" />;
      case 'in_progress': return <Clock size={16} className="text-blue-500" />;
      case 'not_started': return <Circle size={16} className="text-stone-400" />;
      default: return <AlertCircle size={16} className="text-amber-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'completed': 'bg-green-100 text-green-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'not_started': 'bg-stone-100 text-stone-600',
      'delayed': 'bg-red-100 text-red-700',
      'blocked': 'bg-red-100 text-red-700'
    };
    return variants[status] || 'bg-stone-100 text-stone-600';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="client-tasks-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-800">Tasks & Schedule</h1>
            <p className="text-stone-500 mt-1">Track the progress of your project tasks</p>
          </div>
          {/* Real-time connection indicator */}
          <div className="flex items-center gap-2">
            {isConnected ? (
              <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                <Wifi size={12} />
                Live Updates
              </Badge>
            ) : (
              <Badge className="bg-stone-100 text-stone-500 flex items-center gap-1">
                <WifiOff size={12} />
                Offline
              </Badge>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Total Tasks</p>
            <p className="text-2xl font-bold text-stone-800">{stats.total}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Completed</p>
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">In Progress</p>
            <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Not Started</p>
            <p className="text-2xl font-bold text-stone-600">{stats.notStarted}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Total Value</p>
            <p className="text-2xl font-bold text-amber-600">₹{stats.totalValue.toLocaleString()}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-tasks"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-[200px]" data-testid="project-filter">
              <SelectValue placeholder="Project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tasks List */}
        {filteredTasks.length === 0 ? (
          <div className="apple-card p-12 text-center">
            <ListTodo size={48} className="mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-600 mb-2">No Tasks Found</h3>
            <p className="text-stone-400 text-sm">
              {tasks.length === 0 
                ? "No tasks have been assigned to your projects yet."
                : "Try adjusting your search or filters."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTasks.map((task) => (
              <div 
                key={task.id} 
                className="apple-card p-4 hover:shadow-md transition-shadow"
                data-testid={`client-task-${task.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(task.status)}
                      <h3 className="font-semibold text-stone-800">{task.name}</h3>
                      <Badge className={getStatusBadge(task.status)}>
                        {task.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    {task.description && (
                      <p className="text-sm text-stone-500 mb-3 line-clamp-2">{task.description}</p>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500">
                      <span className="flex items-center gap-1">
                        <Package size={14} />
                        {task.project_name}
                      </span>
                      {task.category_name && (
                        <span className="text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full text-xs">
                          {task.category_name}
                        </span>
                      )}
                      {task.start_date && (
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {formatDate(task.start_date)} 
                          {task.end_date && ` - ${formatDate(task.end_date)}`}
                        </span>
                      )}
                    </div>
                    
                    {/* Progress Bar - iter-386u-j: bracket-based colours matching Vendor/Admin */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-stone-500">Progress</span>
                        <span className="font-medium text-stone-700">{task.progress_percentage || 0}%</span>
                      </div>
                      <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressBgClass(task.progress_percentage || 0)} rounded-full transition-all`}
                          style={{ width: `${task.progress_percentage || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right shrink-0">
                    <div className="text-sm text-stone-400 mb-1">
                      {task.quantity} {task.unit}
                    </div>
                    <div className="text-lg font-bold text-amber-600">
                      ₹{(task.client_visible_cost || task.total || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Read-only Notice */}
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> This is a read-only view. Contact your project manager for any changes.
          </p>
        </div>
      </div>
    </Layout>
  );
}
